import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";

import copilotApi from "../api/copilotApi";
import {
  Composer,
  ConfirmationBanner,
  ConversationSidebar,
  EmptyState,
  LoadingMessage,
  PendingActionCard
} from "../components/copilot";
import useCopilotConversation from "../hooks/useCopilotConversation";
import usePendingAction from "../hooks/usePendingAction";
import "../styles/copilot.css";
import Message from "../components/copilot/Message";

function formatDate(value) {
  if (!value) return "";

  try {
    return new Intl.DateTimeFormat(undefined, {
      dateStyle: "medium",
      timeStyle: "short"
    }).format(new Date(value));
  } catch (_error) {
    return "";
  }
}

function getConversationId(conversation) {
  return conversation?.id || conversation?._id || null;
}

function getMessageKey(message, index) {
  return (
    message?._id ||
    message?.id ||
    `${message?.role || "message"}-${message?.createdAt || index}-${index}`
  );
}

function normalizeHistory(conversations) {
  if (!Array.isArray(conversations)) return [];

  return conversations.map((conversation) => ({
    ...conversation,
    id: getConversationId(conversation),
    lastMessageAt: formatDate(conversation?.lastMessageAt)
  }));
}

export default function CopilotWorkspacePage() {
  const {
    conversation: activeConversation,
    setConversation: setActiveConversation,
    messages,
    appendOptimisticMessage
  } = useCopilotConversation();

 const {
  pendingAction,
  pendingActionStatus,
  hasPendingAction,
  isAwaitingApproval,
  registerPendingAction,
  markPendingActionExecuting,
  markPendingActionCompleted,
  markPendingActionFailed,
  clearPendingAction
} = usePendingAction();

  const [prompts, setPrompts] = useState([]);
  const [history, setHistory] = useState([]);
  const [message, setMessage] = useState("");
  const [loadingPage, setLoadingPage] = useState(true);
  const [loadingConversation, setLoadingConversation] = useState(false);
  const [sending, setSending] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const messageEndRef = useRef(null);
  const noticeTimerRef = useRef(null);

  const activeConversationId = getConversationId(activeConversation);
  const isBusy =
    loadingPage ||
    loadingConversation ||
    sending ||
    confirming ||
    clearing;

  const pagePrompts = useMemo(
    () => (Array.isArray(prompts) ? prompts : []),
    [prompts]
  );

  const showNotice = useCallback((text) => {
    if (noticeTimerRef.current) {
      window.clearTimeout(noticeTimerRef.current);
    }

    setNotice(text);

    noticeTimerRef.current = window.setTimeout(() => {
      setNotice("");
      noticeTimerRef.current = null;
    }, 4500);
  }, []);

  useEffect(() => {
    return () => {
      if (noticeTimerRef.current) {
        window.clearTimeout(noticeTimerRef.current);
      }
    };
  }, []);

  const refreshHistory = useCallback(async () => {
    const result = await copilotApi.getHistory();
    setHistory(normalizeHistory(result?.conversations));
  }, []);

  const loadInitialData = useCallback(async () => {
    setLoadingPage(true);
    setError("");

    try {
      const [promptResult, historyResult] = await Promise.all([
        copilotApi.getPrompts(),
        copilotApi.getHistory()
      ]);

      setPrompts(
        Array.isArray(promptResult?.prompts) ? promptResult.prompts : []
      );
      setHistory(normalizeHistory(historyResult?.conversations));
    } catch (loadError) {
      setError(loadError?.message || "Unable to load Ecovanta Copilot.");
    } finally {
      setLoadingPage(false);
    }
  }, []);

  useEffect(() => {
    loadInitialData();
  }, [loadInitialData]);

  useEffect(() => {
    messageEndRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "end"
    });
  }, [messages, sending, confirming, hasPendingAction]);

  const openConversation = useCallback(
    async (conversationId) => {
      if (!conversationId || isBusy) return;

      setLoadingConversation(true);
      setError("");
      setNotice("");
      clearPendingAction();

      try {
        const result = await copilotApi.getConversation(conversationId);
        setActiveConversation(result?.conversation || null);

        if (
          result?.actionStatus === "confirmation_required" &&
          Array.isArray(result?.pendingActions)
        ) {
          registerPendingAction(result);
        }
      } catch (conversationError) {
        setError(
          conversationError?.message || "Unable to load the conversation."
        );
      } finally {
        setLoadingConversation(false);
      }
    },
    [
      clearPendingAction,
      isBusy,
      registerPendingAction,
      setActiveConversation
    ]
  );

  const startNewConversation = useCallback(() => {
    if (isBusy) return;

    setActiveConversation(null);
    setMessage("");
    setError("");
    setNotice("");
    clearPendingAction();
  }, [clearPendingAction, isBusy, setActiveConversation]);

  const submitMessage = useCallback(
    async (event, explicitMessage = null) => {
      event?.preventDefault();

      const cleanMessage = String(explicitMessage ?? message).trim();

      if (!cleanMessage || sending || confirming || hasPendingAction) {
        return;
      }

      setSending(true);
      setError("");
      setNotice("");

      const optimisticUserMessage = {
        id: `optimistic-${Date.now()}`,
        role: "user",
        content: cleanMessage,
        createdAt: new Date().toISOString(),
        optimistic: true
      };

      appendOptimisticMessage(optimisticUserMessage);
      setMessage("");

      try {
        const result = await copilotApi.sendMessage({
          message: cleanMessage,
          conversationId: activeConversationId,
          confirmedToolCall: null
        });

const nextConversation = result?.conversation
  ? {
      ...result.conversation,
      messages:
        result.actionStatus === "confirmation_required"
          ? (result.conversation.messages || []).filter(
              (item, index, items) =>
                !(
                  item?.role === "assistant" &&
                  index === items.length - 1
                )
            )
          : result.conversation.messages || []
    }
  : null;

setActiveConversation(nextConversation);
registerPendingAction(result);
        await refreshHistory();
      } catch (sendError) {
        setError(sendError?.message || "Copilot could not process the request.");

        setActiveConversation((current) => ({
          ...(current || {}),
          messages: (current?.messages || []).filter(
            (item) => item?.id !== optimisticUserMessage.id
          )
        }));

        setMessage(cleanMessage);
      } finally {
        setSending(false);
      }
    },
    [
      activeConversationId,
      appendOptimisticMessage,
      confirming,
      hasPendingAction,
      message,
      refreshHistory,
      registerPendingAction,
      sending,
      setActiveConversation
    ]
  );

const confirmPendingAction = useCallback(async () => {
  if (
    !pendingAction ||
    confirming ||
    sending ||
    pendingActionStatus !== "pending"
  ) {
    return;
  }

  setConfirming(true);
  setError("");
  setNotice("");

  const actionBeingConfirmed = pendingAction;

  markPendingActionExecuting();

  try {
    const result = await copilotApi.confirmAction({
      conversationId: activeConversationId,
      confirmedToolCall: actionBeingConfirmed
    });

    setActiveConversation(
      result?.conversation || null
    );

    markPendingActionCompleted(result);

    showNotice(
      result?.actionStatus === "completed"
        ? "Action completed successfully."
        : "Action confirmed and processed."
    );

    await refreshHistory();
  } catch (confirmationError) {
    markPendingActionFailed(
      confirmationError
    );

    setError(
      confirmationError?.message ||
        "The action could not be completed. You can try again or cancel."
    );
  } finally {
    setConfirming(false);
  }
}, [
  activeConversationId,
  confirming,
  markPendingActionCompleted,
  markPendingActionExecuting,
  markPendingActionFailed,
  pendingAction,
  pendingActionStatus,
  refreshHistory,
  sending,
  setActiveConversation,
  showNotice
]);

  const cancelPendingAction = useCallback(async () => {
    if (!pendingAction || confirming) return;

    setError("");
    setNotice("");

    try {
      await copilotApi.cancelAction({
        conversationId: activeConversationId,
        pendingAction
      });
    } catch (_cancelError) {
      // Cancellation is local-first. A backend cancellation endpoint can be
      // added later without changing the user experience.
    } finally {
      clearPendingAction();
      showNotice("Action cancelled. No changes were made.");
    }
  }, [
    activeConversationId,
    clearPendingAction,
    confirming,
    pendingAction,
    showNotice
  ]);

  useEffect(() => {
    if (!hasPendingAction || confirming) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        cancelPendingAction();
      }

      if (event.key === "Enter" && (event.ctrlKey || event.metaKey)) {
        event.preventDefault();
        confirmPendingAction();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [
    cancelPendingAction,
    confirmPendingAction,
    confirming,
    hasPendingAction
  ]);

  const handleClearHistory = useCallback(async () => {
    if (clearing || sending || confirming) return;

    const confirmed = window.confirm(
      "Clear all Copilot conversation history? This archives the conversations and cannot be undone from this screen."
    );

    if (!confirmed) return;

    setClearing(true);
    setError("");
    setNotice("");

    try {
      await copilotApi.clearHistory();
      setHistory([]);
      setActiveConversation(null);
      setMessage("");
      clearPendingAction();
      showNotice("Copilot conversation history cleared.");
    } catch (clearError) {
      setError(clearError?.message || "Unable to clear Copilot history.");
    } finally {
      setClearing(false);
    }
  }, [
    clearPendingAction,
    clearing,
    confirming,
    sending,
    setActiveConversation,
    showNotice
  ]);

  if (loadingPage) {
    return (
      <main className="copilot-page">
        <div
          role="status"
          aria-live="polite"
          style={{
            maxWidth: 1380,
            margin: "0 auto",
            padding: 40,
            color: "#17342e",
            fontWeight: 700
          }}
        >
          Loading Ecovanta Copilot...
        </div>
      </main>
    );
  }

  return (
    <main className="copilot-page">
      <div
        style={{
          maxWidth: 1380,
          margin: "0 auto",
          padding: 28
        }}
      >
        <header style={{ marginBottom: 22 }}>
          <div
            style={{
              fontSize: 13,
              fontWeight: 800,
              color: "#2c6b5b",
              textTransform: "uppercase",
              letterSpacing: 1.2
            }}
          >
            Ecovanta Intelligence
          </div>

          <h1
            style={{
              margin: "6px 0 8px",
              color: "#17342e",
              fontSize: 34
            }}
          >
            Ecovanta Copilot
          </h1>

          <p
            style={{
              margin: 0,
              color: "#55716a",
              maxWidth: 820,
              lineHeight: 1.6
            }}
          >
            Grounded ESG assistance for reporting, evidence, workflows,
            materiality, disclosures, and controlled workspace actions.
          </p>
        </header>

        {error && (
          <div
            role="alert"
            style={{
              marginBottom: 18,
              padding: "12px 14px",
              borderRadius: 12,
              background: "#fff3f1",
              border: "1px solid #f1c6bf",
              color: "#8a2d20"
            }}
          >
            {error}
          </div>
        )}

        {notice && (
          <div
            role="status"
            aria-live="polite"
            style={{
              marginBottom: 18,
              padding: "12px 14px",
              borderRadius: 12,
              background: "#edf8f3",
              border: "1px solid #bfe0d2",
              color: "#205c49"
            }}
          >
            {notice}
          </div>
        )}

        <section className="copilot-layout" style={{ alignItems: "stretch" }}>
          <div>
            <ConversationSidebar
              history={history}
              activeId={activeConversationId}
              onOpen={openConversation}
              onNewConversation={startNewConversation}
            />

            <button
              type="button"
              onClick={handleClearHistory}
              disabled={clearing || history.length === 0 || confirming}
              style={{
                width: "100%",
                marginTop: 12,
                padding: "10px 12px",
                borderRadius: 10,
                border: "1px solid #d3e0db",
                background: "#ffffff",
                color: "#55716a",
                fontWeight: 700,
                cursor:
                  clearing || history.length === 0 || confirming
                    ? "not-allowed"
                    : "pointer"
              }}
            >
              {clearing ? "Clearing history..." : "Clear conversation history"}
            </button>
          </div>

          <section
            className="copilot-panel"
            aria-label="Copilot conversation"
            style={{
              minHeight: 690,
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
              boxShadow: "0 18px 48px rgba(38, 77, 67, 0.1)"
            }}
          >
            <div
              style={{
                padding: "18px 20px",
                borderBottom: "1px solid #e3ece9",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 18
              }}
            >
              <div>
                <strong style={{ color: "#17342e" }}>
                  {activeConversation?.title || "New Copilot conversation"}
                </strong>

                <div
                  style={{
                    marginTop: 3,
                    color: "#6d8580",
                    fontSize: 12
                  }}
                >
                  Grounded answers with approval controls for consequential
                  actions
                </div>
              </div>

              {(sending || loadingConversation || confirming) && (
                <div
                  role="status"
                  aria-live="polite"
                  style={{
                    fontSize: 12,
                    color: "#2c6b5b",
                    fontWeight: 700,
                    whiteSpace: "nowrap"
                  }}
                >
                  {loadingConversation
                    ? "Loading..."
                    : confirming
                    ? "Executing approved action..."
                    : "Thinking..."}
                </div>
              )}
            </div>

            <div
              style={{
                flex: 1,
                padding: 24,
                overflowY: "auto",
                minHeight: 490
              }}
            >
              {isAwaitingApproval && (
  <ConfirmationBanner
    pendingAction={pendingAction}
    loading={confirming}
    onConfirm={confirmPendingAction}
    onCancel={cancelPendingAction}
  />
)}

{hasPendingAction && (
  <PendingActionCard
    action={pendingAction}
    status={pendingActionStatus}
  />
)}

              {messages.length === 0 && !loadingConversation && (
                <EmptyState
                  prompts={pagePrompts}
                  onPromptSelect={(prompt) => submitMessage(null, prompt)}
                />
              )}

              {messages.map((chatMessage, index) => (
                <Message
                  key={getMessageKey(chatMessage, index)}
                  message={chatMessage}
                />
              ))}

              {sending && <LoadingMessage />}

              <div ref={messageEndRef} />
            </div>

            <div
            aria-disabled={
                isAwaitingApproval ||
                pendingActionStatus === "executing"
              }
              style={{
              opacity:
                isAwaitingApproval ||
                pendingActionStatus === "executing"
                  ? 0.62
                  : 1,

              pointerEvents:
                isAwaitingApproval ||
                pendingActionStatus === "executing"
                  ? "none"
                  : "auto"
              }}
            >
              <Composer
                value={message}
                onChange={setMessage}
                onSend={submitMessage}
                disabled={sending || loadingConversation || confirming}
              />
            </div>

              {isAwaitingApproval && (
              <div
                style={{
                  padding: "0 20px 16px",
                  background: "#fbfdfc",
                  color: "#6d8580",
                  fontSize: 12
                }}
              >
                Review the requested action above. Press Ctrl/Cmd + Enter to
                confirm or Escape to cancel.
              </div>
            )}
          </section>
        </section>
      </div>
    </main>
  );
}